/**
 * Liveness Test Page
 * ==================
 * Standalone test page for challenge-based liveness detection.
 * Access via: /sdk/test-liveness
 *
 * No backend required - tests the client-side face landmark
 * detection and challenge flow in isolation.
 */

import React, { useState } from 'react';
import MediaPipeLivenessStep from './steps/MediaPipeLivenessStep';

export default function MediaPipeTestPage() {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Mock client that just logs (no backend call needed for testing)
  const mockClient = {
    createLivenessSession: async () => ({ session_id: 'test-local' }),
    completeLiveness: async (sessionId, confidence, metadata) => {
      console.log('Liveness complete:', { sessionId, confidence, metadata });
      return { success: true };
    },
  };

  const handleComplete = (livenessResult) => {
    setResult(livenessResult);
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  if (result) {
    return (
      <div style={styles.page}>
        <div style={styles.resultCard}>
          <div style={styles.successIcon}>&#10003;</div>
          <h2 style={styles.heading}>Liveness Check Passed</h2>
          <div style={styles.resultDetails}>
            <div style={styles.resultRow}>
              <span style={styles.label}>Is Live:</span>
              <span style={styles.value}>{String(result.is_live)}</span>
            </div>
            <div style={styles.resultRow}>
              <span style={styles.label}>Confidence:</span>
              <span style={styles.value}>{result.confidence}%</span>
            </div>
          </div>
          <button style={styles.button} onClick={handleReset}>
            Test Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <MediaPipeLivenessStep
        onComplete={handleComplete}
        client={mockClient}
      />
      {error && <p style={styles.error}>{error}</p>}
    </div>
  );
}

const styles = {
  page: {
    '--primary': '#0D9488',
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  heading: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 8px',
  },
  subtext: {
    fontSize: '14px',
    color: '#6B7280',
    margin: '0',
  },
  resultCard: {
    background: '#fff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    textAlign: 'center',
    maxWidth: '360px',
    width: '100%',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#D1FAE5',
    color: '#10B981',
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  resultDetails: {
    margin: '20px 0',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '8px',
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
  },
  label: {
    color: '#6B7280',
    fontSize: '14px',
  },
  value: {
    color: '#1F2937',
    fontSize: '14px',
    fontWeight: '600',
  },
  button: {
    padding: '12px 28px',
    background: 'var(--primary, #0D9488)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '16px',
  },
  error: {
    color: '#EF4444',
    fontSize: '14px',
    marginTop: '12px',
  },
};
